import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { AppLayout } from "@/components/app-layout";
import { PdfFormEditor } from "@/components/PdfFormEditor";
import { getApiUrl } from "@/lib/config";
import { useLanguage } from "@/components/language-provider";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Upload, FileText, Trash2, Pencil, Eye, Link2, Loader2, AlertTriangle, Check, X, Variable, Plug } from "lucide-react";

// ── Types ────────────────────────────────────────────────────
interface FieldMapping {
  variable: string;
  d365Field: string;
  label: string;
}

interface DocTemplate {
  id: string;
  name: string;
  description?: string;
  entityType: string;
  originalFileName: string;
  variables: string[];
  mappings: FieldMapping[];
  signaturePositionsJson?: string;
  formFieldsJson?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormField {
  id: string;
  label: string;
  type: string; // text, date, number, email, checkbox, dropdown
  required: boolean;
  placeholder?: string;
  options?: string[];
  d365Field?: string;
  d365EntityType?: string;
}

interface DocTemplateList {
  id: string;
  name: string;
  description?: string;
  entityType: string;
  isActive: boolean;
  createdAt: string;
}

// ── Where a variable can come from (resolved by the Dynamics component, see TemplateResolver.ts) ──
const SOURCE_SYNTAX: Record<string, { title: string; rows: [string, string][] }> = {
  pt: {
    title: "Como escrever a origem de cada variável",
    rows: [
      ["fullname", "Coluna do registro, como o Dynamics mostra (rótulo de opções, data, moeda)"],
      ["parentcustomerid.name", "Coluna de outro registro, pelo lookup (pode encadear: parentcustomerid.primarycontactid.emailaddress1)"],
      ["$user.fullname", "Usuário do Dynamics que está enviando"],
      ["$today  ·  $now  ·  $record.url", "Data de hoje, data e hora, link do registro"],
      ['firstname + " " + lastname', "Junta colunas e textos"],
      ["mobilephone ?? telephone1", "Usa o primeiro que tiver valor"],
      ['"Texto fixo"', "Texto fixo entre aspas"],
      ["estimatedvalue|money  ·  |extenso", "Formatos: money (R$), extenso (valor por extenso), number, int, date, datetime, long (23 de outubro de 2026), upper, lower, title, first, cpf, cnpj, cep, phone, raw, default:texto"],
    ],
  },
  en: {
    title: "How to write each variable's source",
    rows: [
      ["fullname", "Column of the record, as Dynamics shows it (option labels, dates, currency)"],
      ["parentcustomerid.name", "Column of another record, through the lookup (chains allowed: parentcustomerid.primarycontactid.emailaddress1)"],
      ["$user.fullname", "Dynamics user sending the document"],
      ["$today  ·  $now  ·  $record.url", "Today, date and time, record link"],
      ['firstname + " " + lastname', "Joins columns and text"],
      ["mobilephone ?? telephone1", "Uses the first one with a value"],
      ['"Fixed text"', "Fixed text in quotes"],
      ["estimatedvalue|money  ·  |extenso", "Formats: money (R$), extenso (amount in Portuguese words), number, int, date, datetime, long, upper, lower, title, first, cpf, cnpj, cep, phone, raw, default:text"],
    ],
  },
};

// ── i18n ─────────────────────────────────────────────────────
const translations: Record<string, Record<string, string>> = {
  pt: {
    title: "Templates de Documento",
    subtitle: "Crie modelos Word com variáveis que serão preenchidas automaticamente com dados do Dynamics 365",
    newTemplate: "Novo Template",
    noTemplates: "Nenhum template criado ainda",
    noTemplatesDesc: "Crie seu primeiro template para automatizar o envio de documentos pelo D365",
    name: "Nome do Template",
    namePlaceholder: "Ex: NDA Padrão, Contrato de Serviço",
    nameHint: "Este nome aparecerá no D365 PCF para o usuário selecionar",
    description: "Descrição (opcional)",
    descPlaceholder: "Breve descrição do template",
    entityType: "Entidade D365",
    file: "Arquivo Word (.docx)",
    fileHint: "Use {{variavel}} dentro do Word para campos dinâmicos",
    upload: "Selecionar arquivo",
    create: "Criar Template",
    creating: "Criando...",
    cancel: "Cancelar",
    save: "Salvar",
    saving: "Salvando...",
    delete: "Excluir",
    deleteConfirm: "Tem certeza que deseja excluir este template?",
    active: "Ativo",
    inactive: "Inativo",
    variables: "Variáveis Detectadas",
    variablesHint: "Estas variáveis foram encontradas no documento Word",
    noVariables: "Nenhuma variável {{...}} encontrada no documento",
    mappings: "Mapeamento de Campos",
    mappingsHint: "Defina qual campo do Dynamics 365 corresponde a cada variável do template",
    mappingsDesc: "O componente do Dynamics lê estes valores do registro (inclusive por lookups) e preenche o documento. Antes de gerar, o usuário confere e pode ajustar cada valor.",
    variableCol: "Variável no Word",
    d365FieldCol: "Origem no Dynamics (coluna ou expressão)",
    labelCol: "Label (nome amigável)",
    d365FieldPlaceholder: "ex.: fullname · parentcustomerid.name · $today|long",
    labelPlaceholder: "ex: Nome Completo",
    saveMappings: "Salvar Mapeamentos",
    builtIn: "automático",
    builtInHint: "Variáveis automáticas são preenchidas pelo sistema, não precisam de mapeamento",
    templateRules: "Regras para criar templates",
    rule1: "Crie o documento no Word (.docx)",
    rule2: "Use {{nome_da_variavel}} onde quiser um campo dinâmico",
    rule3: "Nomes: só letras, números e underscore (sem espaço)",
    rule4: "Variáveis automáticas: {{data_hoje}}, {{data_today}}, {{hora_atual}}, {{ano_atual}}",
    rule5: "O sistema detecta as variáveis automaticamente no upload",
    example: "Exemplo",
    exampleText: "Contratante: {{nome_completo}}\nCPF: {{cpf}}\nEmpresa: {{empresa}}\nData: {{data_hoje}}",
    entity_contact: "Contact",
    entity_account: "Account",
    entity_opportunity: "Opportunity",
    entity_lead: "Lead",
    entity_quote: "Quote",
    entity_order: "Order",
    entity_invoice: "Invoice",
    entity_custom: "Custom Entity",
    back: "Voltar",
    formFields: "Campos para Preenchimento",
    formFieldsDesc: "Campos que o signatário deve preencher durante a assinatura (opcional). Os valores preenchidos serão enviados de volta ao D365.",
    addField: "Adicionar Campo",
    fieldLabel: "Rótulo",
    fieldType: "Tipo",
    fieldRequired: "Obrigatório",
    fieldD365: "Campo D365",
    fieldPlaceholder: "Placeholder",
    fieldOptions: "Opções (vírgula)",
    saveFields: "Salvar Campos",
    noFields: "Nenhum campo configurado. O signatário assina sem preencher informações extras.",
    typeText: "Texto",
    typeDate: "Data",
    typeNumber: "Número",
    typeEmail: "Email",
    typeCheckbox: "Checkbox",
    typeDropdown: "Dropdown",
    details: "Detalhes",
    editTemplate: "Editar Template",
    createdAt: "Criado em",
    updatedAt: "Atualizado em",
    fileName: "Arquivo",
    status: "Status",
  },
  en: {
    title: "Document Templates",
    subtitle: "Create Word templates with variables that auto-fill with Dynamics 365 data",
    newTemplate: "New Template",
    noTemplates: "No templates created yet",
    noTemplatesDesc: "Create your first template to automate document sending from D365",
    name: "Template Name",
    namePlaceholder: "E.g.: Standard NDA, Service Agreement",
    nameHint: "This name will appear in D365 PCF for users to select",
    description: "Description (optional)",
    descPlaceholder: "Brief template description",
    entityType: "D365 Entity",
    file: "Word File (.docx)",
    fileHint: "Use {{variable}} inside Word for dynamic fields",
    upload: "Select file",
    create: "Create Template",
    creating: "Creating...",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving...",
    delete: "Delete",
    deleteConfirm: "Are you sure you want to delete this template?",
    active: "Active",
    inactive: "Inactive",
    variables: "Detected Variables",
    variablesHint: "These variables were found in the Word document",
    noVariables: "No {{...}} variables found in the document",
    mappings: "Field Mappings",
    mappingsHint: "Define which Dynamics 365 field corresponds to each template variable",
    mappingsDesc: "The Dynamics component reads these values from the record (lookups included) and fills the document. Before generating, the user checks and can adjust each value.",
    variableCol: "Variable in Word",
    d365FieldCol: "Dynamics source (column or expression)",
    labelCol: "Label (friendly name)",
    d365FieldPlaceholder: "e.g.: fullname · parentcustomerid.name · $today|long",
    labelPlaceholder: "e.g.: Full Name",
    saveMappings: "Save Mappings",
    builtIn: "auto",
    builtInHint: "Auto variables are filled by the system, no mapping needed",
    templateRules: "Template creation rules",
    rule1: "Create the document in Word (.docx)",
    rule2: "Use {{variable_name}} where you want a dynamic field",
    rule3: "Names: only letters, numbers and underscore (no spaces)",
    rule4: "Auto variables: {{data_hoje}}, {{data_today}}, {{hora_atual}}, {{ano_atual}}",
    rule5: "The system auto-detects variables on upload",
    example: "Example",
    exampleText: "Contractor: {{full_name}}\nID: {{tax_id}}\nCompany: {{company}}\nDate: {{data_today}}",
    entity_contact: "Contact",
    entity_account: "Account",
    entity_opportunity: "Opportunity",
    entity_lead: "Lead",
    entity_quote: "Quote",
    entity_order: "Order",
    entity_invoice: "Invoice",
    entity_custom: "Custom Entity",
    back: "Back",
    formFields: "Form Fields",
    formFieldsDesc: "Fields the signer must fill during signing (optional). Filled values are sent back to D365.",
    addField: "Add Field",
    fieldLabel: "Label",
    fieldType: "Type",
    fieldRequired: "Required",
    fieldD365: "D365 Field",
    fieldPlaceholder: "Placeholder",
    fieldOptions: "Options (comma)",
    saveFields: "Save Fields",
    noFields: "No fields configured. Signer signs without filling extra information.",
    typeText: "Text",
    typeDate: "Date",
    typeNumber: "Number",
    typeEmail: "Email",
    typeCheckbox: "Checkbox",
    typeDropdown: "Dropdown",
    details: "Details",
    editTemplate: "Edit Template",
    createdAt: "Created at",
    updatedAt: "Updated at",
    fileName: "File",
    status: "Status",
  },
};

const BUILTIN_VARS = ["data_hoje", "data_today", "hora_atual", "ano_atual"];
const ENTITY_TYPES = ["contact", "account", "opportunity", "lead", "quote", "order", "invoice"];

// ── API helpers ──────────────────────────────────────────────
async function fetchTemplates(): Promise<DocTemplateList[]> {
  const res = await apiRequest("GET", "/api/v1/document-templates");
  const json = await res.json();
  return json?.data ?? [];
}

async function fetchTemplate(id: string): Promise<DocTemplate> {
  const res = await apiRequest("GET", `/api/v1/document-templates/${id}`);
  const json = await res.json();
  return json?.data;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = r.result as string; resolve(s.includes(",") ? s.split(",")[1] : s); };
    r.onerror = () => reject(new Error("Failed to read file"));
    r.readAsDataURL(file);
  });
}

// ── Component ────────────────────────────────────────────────
export default function DocumentTemplates() {
  const [, setLocation] = useLocation();
  const { language } = useLanguage();
  const t = translations[language] ?? translations.pt;
  const { toast } = useToast();

  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) { setLocation("/login"); return; }
    setUser(JSON.parse(stored));
  }, [setLocation]);
  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("user"); setLocation("/"); };

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showDeleteId, setShowDeleteId] = useState<string | null>(null);

  // ── Create form state ──
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createEntity, setCreateEntity] = useState("contact");
  const [createFile, setCreateFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── List query ──
  const { data: templates = [], isLoading } = useQuery<DocTemplateList[]>({
    queryKey: ["/api/v1/document-templates"],
    queryFn: fetchTemplates,
  });

  // ── Detail query (for editing) ──
  const { data: editingTemplate, isLoading: loadingDetail } = useQuery<DocTemplate>({
    queryKey: ["/api/v1/document-templates", editingId],
    queryFn: () => fetchTemplate(editingId!),
    enabled: !!editingId,
  });

  // ── Create mutation ──
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!createFile) throw new Error("File required");
      const base64 = await fileToBase64(createFile);
      const res = await apiRequest("POST", "/api/v1/document-templates", {
        name: createName.trim(),
        description: createDesc.trim() || null,
        entityType: createEntity,
        fileBase64: base64,
        fileName: createFile.name,
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message ?? "Failed"); }
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: language === "pt" ? "Modelo criado" : "Template created", description: language === "pt" ? `${data?.data?.variables?.length ?? 0} variáveis encontradas` : `${data?.data?.variables?.length ?? 0} variables detected` });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/document-templates"] });
      setShowCreate(false);
      resetCreateForm();
      if (data?.data?.id) setEditingId(data.data.id);
    },
    onError: (err: Error) => toast({ title: language === "pt" ? "Erro" : "Error", description: err.message, variant: "destructive" }),
  });

  // ── Toggle active mutation ──
  const toggleMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      await apiRequest("PATCH", `/api/v1/document-templates/${id}`, { isActive: active });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/v1/document-templates"] }),
  });

  // ── Save mappings mutation ──
  const saveMappingsMutation = useMutation({
    mutationFn: async ({ id, mappings }: { id: string; mappings: FieldMapping[] }) => {
      await apiRequest("PUT", `/api/v1/document-templates/${id}/mappings`, { mappings });
    },
    onSuccess: () => {
      toast({ title: t.saveMappings, description: "OK" });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/document-templates", editingId] });
    },
    onError: (err: Error) => toast({ title: language === "pt" ? "Erro" : "Error", description: err.message, variant: "destructive" }),
  });

  const saveFormFieldsMutation = useMutation({
    mutationFn: async ({ id, fields }: { id: string; fields: FormField[] }) => {
      await apiRequest("PATCH", `/api/v1/document-templates/${id}`, { formFieldsJson: JSON.stringify(fields) });
    },
    onSuccess: () => {
      toast({ title: t.saveFields, description: "OK" });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/document-templates", editingId] });
    },
    onError: (err: Error) => toast({ title: language === "pt" ? "Erro" : "Error", description: err.message, variant: "destructive" }),
  });

  // ── Delete mutation ──
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await apiRequest("DELETE", `/api/v1/document-templates/${id}`); },
    onSuccess: () => {
      toast({ title: t.delete, description: "OK" });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/document-templates"] });
      setShowDeleteId(null);
      setEditingId(null);
    },
  });

  const resetCreateForm = () => {
    setCreateName(""); setCreateDesc(""); setCreateEntity("contact"); setCreateFile(null);
  };

  // ── Mapping editor state ──
  const [localMappings, setLocalMappings] = useState<FieldMapping[]>([]);
  const [localFormFields, setLocalFormFields] = useState<FormField[]>([]);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [editorPdfUrl, setEditorPdfUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const openFieldEditor = async () => {
    if (!editingId) return;
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getApiUrl(`/api/v1/document-templates/${editingId}/preview`), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      const b64 = data?.data?.pdfBase64 || data?.pdfBase64;
      if (!b64) throw new Error("No PDF");
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setEditorPdfUrl(url);
      setShowFieldEditor(true);
    } catch (err) {
      toast({ title: language === "pt" ? "Erro" : "Error", description: language === "pt" ? "Não foi possível carregar a pré-visualização do PDF" : "Failed to load PDF preview", variant: "destructive" });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Sync local mappings when editing template loads
  const syncMappings = useCallback(() => {
    if (!editingTemplate) return;
    const vars = editingTemplate.variables.filter(v => !BUILTIN_VARS.includes(v));
    const existing = editingTemplate.mappings ?? [];
    setLocalMappings(vars.map(v => {
      const ex = existing.find(m => m.variable === v);
      return { variable: v, d365Field: ex?.d365Field ?? "", label: ex?.label ?? "" };
    }));
    // Sync form fields
    try {
      const fields = editingTemplate.formFieldsJson ? JSON.parse(editingTemplate.formFieldsJson) : [];
      setLocalFormFields(Array.isArray(fields) ? fields : []);
    } catch { setLocalFormFields([]); }
  }, [editingTemplate]);

  // Trigger sync when detail loads
  if (editingTemplate && localMappings.length === 0 && editingTemplate.variables.length > 0) {
    syncMappings();
  }

  // ── Render: list ──
  if (!user) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">{language === "pt" ? "Carregando..." : "Loading..."}</div></div>;

  if (!editingId) {
    return (
      <AppLayout user={user} onLogout={handleLogout} currentPage="/document-templates">
        <div className="max-w-4xl mx-auto p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">{t.title}</h1>
              <p className="text-muted-foreground text-sm mt-1">{t.subtitle}</p>
            </div>
            <Button onClick={() => setShowCreate(true)} data-testid="btn-new-template">
              <Plus className="h-4 w-4 mr-2" /> {t.newTemplate}
            </Button>
          </div>

          {/* Rules card */}
          <Card className="mb-6 border-dashed">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" /> {t.templateRules}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-1">
              <p>1. {t.rule1}</p>
              <p>2. {t.rule2}</p>
              <p>3. {t.rule3}</p>
              <p>4. {t.rule4}</p>
              <p>5. {t.rule5}</p>
              <div className="mt-3 p-3 bg-muted rounded-md font-mono text-xs">
                <div className="font-semibold mb-1">{t.example}:</div>
                <pre className="whitespace-pre-wrap">{t.exampleText}</pre>
              </div>
            </CardContent>
          </Card>

          {/* Template list */}
          {isLoading ? (
            <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : templates.length === 0 ? (
            <Card className="text-center p-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold">{t.noTemplates}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t.noTemplatesDesc}</p>
              <Button className="mt-4" onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-2" /> {t.newTemplate}
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {templates.map(tmpl => (
                <Card key={tmpl.id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setEditingId(tmpl.id)}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{tmpl.name}</div>
                        {tmpl.description && <div className="text-xs text-muted-foreground">{tmpl.description}</div>}
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {t[`entity_${tmpl.entityType}`] ?? tmpl.entityType} · {new Date(tmpl.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={tmpl.isActive ? "default" : "secondary"}>
                        {tmpl.isActive ? t.active : t.inactive}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Create dialog */}
          <Dialog open={showCreate} onOpenChange={(open) => { if (!open) { setShowCreate(false); resetCreateForm(); } }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5" /> {t.newTemplate}</DialogTitle>
                <DialogDescription>{t.subtitle}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>{t.name}</Label>
                  <Input value={createName} onChange={e => setCreateName(e.target.value)} placeholder={t.namePlaceholder} data-testid="input-template-name" />
                  <p className="text-xs text-muted-foreground">{t.nameHint}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t.description}</Label>
                  <Textarea value={createDesc} onChange={e => setCreateDesc(e.target.value)} placeholder={t.descPlaceholder} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>{t.entityType}</Label>
                  <Select value={createEntity} onValueChange={setCreateEntity}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map(et => (
                        <SelectItem key={et} value={et}>{t[`entity_${et}`] ?? et}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.file}</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={createFile?.name ?? ""} placeholder=".docx" className="flex-1" />
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-2" /> {t.upload}
                    </Button>
                  </div>
                  <input ref={fileInputRef} type="file" accept=".docx" className="hidden" onChange={e => { if (e.target.files?.[0]) setCreateFile(e.target.files[0]); }} />
                  <p className="text-xs text-muted-foreground">{t.fileHint}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowCreate(false); resetCreateForm(); }}>{t.cancel}</Button>
                <Button onClick={() => createMutation.mutate()} disabled={!createName.trim() || !createFile || createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  {createMutation.isPending ? t.creating : t.create}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete confirm dialog */}
          <Dialog open={!!showDeleteId} onOpenChange={open => { if (!open) setShowDeleteId(null); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>{t.delete}</DialogTitle><DialogDescription>{t.deleteConfirm}</DialogDescription></DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteId(null)}>{t.cancel}</Button>
                <Button variant="destructive" onClick={() => { if (showDeleteId) deleteMutation.mutate(showDeleteId); }} disabled={deleteMutation.isPending}>
                  {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t.delete}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </AppLayout>
    );
  }

  // ── Render: detail / edit ──
  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/document-templates">
      <div className="max-w-4xl mx-auto p-6">
        <Button variant="ghost" className="mb-4" onClick={() => { setEditingId(null); setLocalMappings([]); setLocalFormFields([]); }}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t.back}
        </Button>

        {loadingDetail ? (
          <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : editingTemplate ? (
          <div className="space-y-6">
            {/* Header card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" /> {editingTemplate.name}
                    </CardTitle>
                    {editingTemplate.description && <CardDescription>{editingTemplate.description}</CardDescription>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editingTemplate.isActive}
                        onCheckedChange={(checked) => toggleMutation.mutate({ id: editingTemplate.id, active: checked })}
                      />
                      <span className="text-sm">{editingTemplate.isActive ? t.active : t.inactive}</span>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => setShowDeleteId(editingTemplate.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">{t.entityType}:</span> <Badge variant="outline">{t[`entity_${editingTemplate.entityType}`] ?? editingTemplate.entityType}</Badge></div>
                <div><span className="text-muted-foreground">{t.fileName}:</span> {editingTemplate.originalFileName}</div>
                <div><span className="text-muted-foreground">{t.createdAt}:</span> {new Date(editingTemplate.createdAt).toLocaleString()}</div>
                <div><span className="text-muted-foreground">{t.updatedAt}:</span> {new Date(editingTemplate.updatedAt).toLocaleString()}</div>
              </CardContent>
            </Card>

            {/* Variables card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Variable className="h-4 w-4" /> {t.variables}
                </CardTitle>
                <CardDescription>{t.variablesHint}</CardDescription>
              </CardHeader>
              <CardContent>
                {editingTemplate.variables.length === 0 ? (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> {t.noVariables}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {editingTemplate.variables.map(v => (
                      <Badge key={v} variant={BUILTIN_VARS.includes(v) ? "secondary" : "outline"} className="font-mono text-xs">
                        {"{{" + v + "}}"}
                        {BUILTIN_VARS.includes(v) && <span className="ml-1 text-[10px] opacity-60">{t.builtIn}</span>}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Field mappings card */}
            {editingTemplate.variables.filter(v => !BUILTIN_VARS.includes(v)).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Plug className="h-4 w-4" /> {t.mappings}
                  </CardTitle>
                  <CardDescription>{t.mappingsDesc}</CardDescription>
                </CardHeader>
                <CardContent>
                  <details className="mb-4 rounded-md border bg-muted/30 px-3 py-2 text-xs" data-testid="mapping-syntax-help">
                    <summary className="cursor-pointer font-medium">{(SOURCE_SYNTAX[language] ?? SOURCE_SYNTAX.pt).title}</summary>
                    <div className="mt-2 grid gap-1.5">
                      {(SOURCE_SYNTAX[language] ?? SOURCE_SYNTAX.pt).rows.map(([code, text]) => (
                        <div key={code} className="grid gap-x-3 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
                          <code className="font-mono text-[11px] text-primary break-all">{code}</code>
                          <span className="text-muted-foreground">{text}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="grid grid-cols-3 gap-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      <div>{t.variableCol}</div>
                      <div>{t.d365FieldCol}</div>
                      <div>{t.labelCol}</div>
                    </div>

                    {/* Mapping rows */}
                    {localMappings.map((mapping, idx) => (
                      <div key={mapping.variable} className="grid grid-cols-3 gap-3 items-center">
                        <div className="font-mono text-sm bg-muted px-2 py-1.5 rounded">
                          {"{{" + mapping.variable + "}}"}
                        </div>
                        <Input
                          value={mapping.d365Field}
                          onChange={e => {
                            const updated = [...localMappings];
                            updated[idx] = { ...updated[idx], d365Field: e.target.value };
                            setLocalMappings(updated);
                          }}
                          placeholder={t.d365FieldPlaceholder}
                          className="font-mono text-xs"
                          data-testid={`input-mapping-field-${idx}`}
                        />
                        <Input
                          value={mapping.label}
                          onChange={e => {
                            const updated = [...localMappings];
                            updated[idx] = { ...updated[idx], label: e.target.value };
                            setLocalMappings(updated);
                          }}
                          placeholder={t.labelPlaceholder}
                          className="text-xs"
                          data-testid={`input-mapping-label-${idx}`}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t flex justify-end">
                    <Button
                      onClick={() => saveMappingsMutation.mutate({ id: editingTemplate.id, mappings: localMappings })}
                      disabled={saveMappingsMutation.isPending}
                    >
                      {saveMappingsMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                      {t.saveMappings}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Form Fields card — optional fields signer fills during signing */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Plug className="h-4 w-4" /> {t.formFields}
                </CardTitle>
                <CardDescription>{t.formFieldsDesc}</CardDescription>
              </CardHeader>
              <CardContent>
                {localFormFields.length === 0 ? (
                  <p className="text-sm text-muted-foreground mb-4">{t.noFields}</p>
                ) : (
                  <div className="space-y-3 mb-4">
                    {localFormFields.map((field, idx) => (
                      <div key={field.id} className="p-3 border rounded-lg space-y-2 bg-muted/30">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-muted-foreground">#{idx + 1} — {field.id}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setLocalFormFields(prev => prev.filter((_, i) => i !== idx))}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">{t.fieldLabel}</Label>
                            <Input className="h-8 text-xs" value={field.label} onChange={e => {
                              const u = [...localFormFields]; u[idx] = { ...u[idx], label: e.target.value }; setLocalFormFields(u);
                            }} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{t.fieldType}</Label>
                            <Select value={field.type} onValueChange={v => {
                              const u = [...localFormFields]; u[idx] = { ...u[idx], type: v }; setLocalFormFields(u);
                            }}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">{t.typeText}</SelectItem>
                                <SelectItem value="date">{t.typeDate}</SelectItem>
                                <SelectItem value="number">{t.typeNumber}</SelectItem>
                                <SelectItem value="email">{t.typeEmail}</SelectItem>
                                <SelectItem value="checkbox">{t.typeCheckbox}</SelectItem>
                                <SelectItem value="dropdown">{t.typeDropdown}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{t.fieldD365}</Label>
                            <Input className="h-8 text-xs font-mono" placeholder="address1_line1" value={field.d365Field || ""} onChange={e => {
                              const u = [...localFormFields]; u[idx] = { ...u[idx], d365Field: e.target.value }; setLocalFormFields(u);
                            }} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">{t.fieldPlaceholder}</Label>
                            <Input className="h-8 text-xs" value={field.placeholder || ""} onChange={e => {
                              const u = [...localFormFields]; u[idx] = { ...u[idx], placeholder: e.target.value }; setLocalFormFields(u);
                            }} />
                          </div>
                          {field.type === "dropdown" && (
                            <div className="space-y-1">
                              <Label className="text-xs">{t.fieldOptions}</Label>
                              <Input className="h-8 text-xs" placeholder="Opt1, Opt2, Opt3" value={(field.options || []).join(", ")} onChange={e => {
                                const u = [...localFormFields]; u[idx] = { ...u[idx], options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }; setLocalFormFields(u);
                              }} />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch checked={field.required} onCheckedChange={v => {
                            const u = [...localFormFields]; u[idx] = { ...u[idx], required: v }; setLocalFormFields(u);
                          }} />
                          <span className="text-xs">{t.fieldRequired}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      const newId = `f${Date.now().toString(36)}`;
                      setLocalFormFields(prev => [...prev, { id: newId, label: "", type: "text", required: true }]);
                    }}>
                      <Plus className="h-4 w-4 mr-1" /> {t.addField}
                    </Button>
                    <Button variant="default" size="sm" onClick={openFieldEditor} disabled={loadingPreview}>
                      {loadingPreview ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                      {language === "pt" ? "Posicionar no PDF" : "Position on PDF"}
                    </Button>
                  </div>
                  <Button size="sm"
                    onClick={() => saveFormFieldsMutation.mutate({ id: editingTemplate.id, fields: localFormFields })}
                    disabled={saveFormFieldsMutation.isPending}
                  >
                    {saveFormFieldsMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    {t.saveFields}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}

        {/* Visual PDF Form Field Editor */}
        {showFieldEditor && editorPdfUrl && (
          <PdfFormEditor
            pdfUrl={editorPdfUrl}
            fields={localFormFields}
            onFieldsChange={setLocalFormFields}
            onClose={() => { setShowFieldEditor(false); if (editorPdfUrl) URL.revokeObjectURL(editorPdfUrl); setEditorPdfUrl(null); }}
            onSave={(fields) => {
              if (editingId) {
                saveFormFieldsMutation.mutate({ id: editingId, fields });
                setShowFieldEditor(false);
                if (editorPdfUrl) URL.revokeObjectURL(editorPdfUrl);
                setEditorPdfUrl(null);
              }
            }}
            saving={saveFormFieldsMutation.isPending}
            language={language}
          />
        )}

        {/* Delete confirm (also available in detail view) */}
        <Dialog open={!!showDeleteId} onOpenChange={open => { if (!open) setShowDeleteId(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t.delete}</DialogTitle><DialogDescription>{t.deleteConfirm}</DialogDescription></DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteId(null)}>{t.cancel}</Button>
              <Button variant="destructive" onClick={() => { if (showDeleteId) deleteMutation.mutate(showDeleteId); }} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t.delete}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
